import { clamp } from "lodash-es";
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getTabBarScrollState } from "web_myplann/Shared/Utils/getTabBarScrollState";
import { device } from "web_myplann/Utils/device";

export const useTabBarCollapseOnScroll = (enabled: boolean) => {
	const { pathname } = useLocation();
	const collapsedRef = useRef<boolean>(undefined);
	const scrollTopsRef = useRef(new WeakMap<Element, number>());
	const anchorsRef = useRef(new WeakMap<Element, number>());

	const setCollapsed = useCallback((collapsed: boolean) => {
		if (collapsedRef.current === collapsed) return;
		collapsedRef.current = collapsed;
		device.setTabBarCollapsed(collapsed).catch(() => {});
	}, []);

	useEffect(() => {
		setCollapsed(false);
	}, [pathname, setCollapsed]);

	useEffect(() => {
		if (!enabled) return;
		const scrollTops = scrollTopsRef.current;
		const anchors = anchorsRef.current;

		const handleScroll = ({ target }: Event) => {
			const element =
				target instanceof Document ? target.scrollingElement : target;
			if (!(element instanceof Element)) return;
			const maxScrollTop = element.scrollHeight - element.clientHeight;
			const scrollTop = clamp(element.scrollTop, 0, Math.max(maxScrollTop, 0));
			if (scrollTop === (scrollTops.get(element) ?? 0)) return;
			scrollTops.set(element, scrollTop);

			const { collapsed, anchor } = getTabBarScrollState(
				{
					collapsed: collapsedRef.current ?? false,
					anchor: anchors.get(element) ?? 0,
				},
				scrollTop,
			);
			anchors.set(element, anchor);
			setCollapsed(collapsed);
		};

		document.addEventListener("scroll", handleScroll, {
			capture: true,
			passive: true,
		});
		return () => {
			document.removeEventListener("scroll", handleScroll, { capture: true });
		};
	}, [enabled, setCollapsed]);
};
